import React, { useEffect, useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControlLabel,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import PauseCircleOutlineOutlinedIcon from '@mui/icons-material/PauseCircleOutlineOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import {
    approveHourReportingWorkflow,
    getHourReportingWorkflow,
    HourReportingWorkflow,
    HourReportingWorkflowDetail,
    listHourReportingWorkflows,
    pauseHourReportingWorkflow,
    resumeHourReportingWorkflow,
    sendHourReportingAdminMessage,
} from '../api/hourReportingApi';
import axiosInstance from '../api/axiosInstance';

type DraftCommandLine = {
    date: string;
    startTime: string | null;
    endTime: string | null;
    breakMinutes: number | null;
    workedMinutes: number | null;
    remarks: string | null;
    looncomponentId: number | null;
};

type JsonAccordionProps = {
    title: string;
    subtitle?: string | null;
    value: unknown;
    defaultExpanded?: boolean;
};

type DetailRowProps = {
    label: string;
    value: React.ReactNode;
};

type WorkflowAttachment = {
    type?: string | null;
    caption?: string | null;
    mediaId?: string | null;
    fileName?: string | null;
    mimeType?: string | null;
    sizeBytes?: number | null;
    savedFileId?: number | null;
};

type ConversationEntry = {
    id: string;
    sender: 'worker' | 'assistant' | 'admin' | 'system';
    title: string;
    body?: string | null;
    createdAt?: string | null;
    attachment?: WorkflowAttachment | null;
};

type HoursBlockStatus = 'reported' | 'disputed' | 'corrected' | 'approved';

type HoursDiagramBlock = {
    id: string;
    status: HoursBlockStatus;
    date: string;
    dayIndex: number;
    startMinutes: number;
    endMinutes: number;
    timeLabel: string;
    workedLabel: string;
    remarks?: string | null;
};

type HoursDiagramData = {
    weekDates: string[];
    blocks: HoursDiagramBlock[];
    minHour: number;
    maxHour: number;
    missingLineCount: number;
    hasAttentionState: boolean;
};

const TECHNICAL_MODE_STORAGE_KEY = 'optima:hours-reporting:technical-mode';

function formatWorkedMinutes(minutes: number | null | undefined): string {
    if (!Number.isFinite(Number(minutes)) || Number(minutes) <= 0) {
        return 'missing';
    }

    const value = Number(minutes);
    return `${Math.floor(value / 60)}h ${value % 60}m`;
}

function formatDateTime(value: string | null | undefined): string {
    if (!value) {
        return 'missing';
    }

    const timestamp = new Date(value);
    return Number.isNaN(timestamp.getTime()) ? String(value) : timestamp.toLocaleString();
}

function formatPrimitive(value: unknown): string {
    if (value === null || value === undefined || value === '') {
        return 'missing';
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }

    return String(value);
}

function formatFileSize(bytes: number | null | undefined): string {
    if (!Number.isFinite(Number(bytes)) || Number(bytes) <= 0) {
        return 'unknown size';
    }

    const value = Number(bytes);
    if (value < 1024) {
        return `${value} B`;
    }
    if (value < 1024 * 1024) {
        return `${(value / 1024).toFixed(1)} KB`;
    }
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function formatWeekdayLabel(isoDate: string): string {
    const date = new Date(`${isoDate}T00:00:00Z`);
    return date.toLocaleDateString('en-GB', { weekday: 'long', timeZone: 'UTC' });
}

function formatShortDayLabel(isoDate: string): string {
    const date = new Date(`${isoDate}T00:00:00Z`);
    return date.toLocaleDateString('en-GB', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function addDaysIso(isoDate: string, days: number): string {
    const date = new Date(`${isoDate}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
}

function getWeekStartIso(isoDate: string): string {
    const date = new Date(`${isoDate}T00:00:00Z`);
    const day = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() - (day - 1));
    return date.toISOString().slice(0, 10);
}

function getDayIndexInWeek(weekStartIso: string, isoDate: string): number {
    const start = new Date(`${weekStartIso}T00:00:00Z`).getTime();
    const current = new Date(`${isoDate}T00:00:00Z`).getTime();
    return Math.round((current - start) / 86400000);
}

function toMinutesFromTime(value: string | null | undefined): number | null {
    const match = String(value || '').match(/^(\d{2}):(\d{2})$/);
    if (!match) {
        return null;
    }
    return (Number(match[1]) * 60) + Number(match[2]);
}

function getLineSignature(line: DraftCommandLine): string {
    return [
        line.startTime || '',
        line.endTime || '',
        line.breakMinutes ?? '',
        line.remarks || '',
    ].join('|');
}

function getLineTimeLabel(line: DraftCommandLine): string {
    return line.startTime && line.endTime ? `${line.startTime}-${line.endTime}` : 'Time missing';
}

function buildHoursDiagramData(params: {
    declarationPeriodStart?: string | null;
    extractedLines: DraftCommandLine[];
    confirmedLines: DraftCommandLine[];
    isApproved: boolean;
    hasAttentionState: boolean;
}): HoursDiagramData {
    const { declarationPeriodStart, extractedLines, confirmedLines, isApproved, hasAttentionState } = params;
    const datedLines = [...extractedLines, ...confirmedLines].filter((line) => Boolean(line.date));
    const anchorDate = declarationPeriodStart || datedLines[0]?.date || new Date().toISOString().slice(0, 10);
    const weekStart = getWeekStartIso(anchorDate);
    const weekDates = Array.from({ length: 7 }, (_, index) => addDaysIso(weekStart, index));
    const confirmedByDate = new Map(confirmedLines.map((line) => [line.date, line]));
    const blocks: HoursDiagramBlock[] = [];
    let missingLineCount = 0;

    const pushBlock = (line: DraftCommandLine, status: HoursBlockStatus, suffix: string) => {
        const startMinutes = toMinutesFromTime(line.startTime);
        const endMinutes = toMinutesFromTime(line.endTime);
        const dayIndex = getDayIndexInWeek(weekStart, line.date);

        if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes) || endMinutes <= startMinutes || dayIndex < 0 || dayIndex > 6) {
            missingLineCount += 1;
            return;
        }

        blocks.push({
            id: `${suffix}-${line.date}-${line.startTime || 'na'}-${line.endTime || 'na'}-${status}`,
            status,
            date: line.date,
            dayIndex,
            startMinutes,
            endMinutes,
            timeLabel: getLineTimeLabel(line),
            workedLabel: formatWorkedMinutes(line.workedMinutes),
            remarks: line.remarks,
        });
    };

    extractedLines.forEach((line, index) => {
        if (confirmedLines.length === 0) {
            pushBlock(line, isApproved ? 'approved' : 'reported', `reported-${index}`);
            return;
        }

        const confirmedLine = confirmedByDate.get(line.date);
        if (!confirmedLine) {
            pushBlock(line, 'disputed', `disputed-${index}`);
            return;
        }

        const unchanged = getLineSignature(line) === getLineSignature(confirmedLine);
        if (unchanged) {
            if (!isApproved) {
                pushBlock(line, 'reported', `reported-${index}`);
            }
            return;
        }

        pushBlock(line, 'disputed', `disputed-${index}`);
    });

    confirmedLines.forEach((line, index) => {
        const extractedLine = extractedLines.find((candidate) => candidate.date === line.date);
        const unchanged = extractedLine ? getLineSignature(extractedLine) === getLineSignature(line) : false;

        if (unchanged && !isApproved) {
            return;
        }

        pushBlock(line, isApproved ? 'approved' : 'corrected', `final-${index}`);
    });

    const plottedBlocks = blocks.filter((block) => Number.isFinite(block.startMinutes) && Number.isFinite(block.endMinutes));
    const minMinutes = plottedBlocks.length
        ? Math.min(...plottedBlocks.map((block) => block.startMinutes))
        : 6 * 60;
    const maxMinutes = plottedBlocks.length
        ? Math.max(...plottedBlocks.map((block) => block.endMinutes))
        : 20 * 60;

    return {
        weekDates,
        blocks: plottedBlocks,
        minHour: Math.max(0, Math.floor(minMinutes / 60) - 1),
        maxHour: Math.min(24, Math.ceil(maxMinutes / 60) + 1),
        missingLineCount,
        hasAttentionState,
    };
}

function getHoursBlockStyle(status: HoursBlockStatus): Record<string, string | number> {
    switch (status) {
        case 'approved':
            return {
                backgroundColor: 'rgba(46, 125, 50, 0.24)',
                borderColor: '#2e7d32',
                color: '#1b5e20',
                left: '18%',
                right: '18%',
                zIndex: 4,
                borderStyle: 'solid',
            };
        case 'corrected':
            return {
                backgroundColor: 'rgba(2, 136, 209, 0.18)',
                borderColor: '#0288d1',
                color: '#01579b',
                left: '12%',
                right: '12%',
                zIndex: 3,
                borderStyle: 'solid',
            };
        case 'disputed':
            return {
                backgroundColor: 'rgba(211, 47, 47, 0.16)',
                borderColor: '#d32f2f',
                color: '#8e1c1c',
                left: '8%',
                right: '8%',
                zIndex: 2,
                borderStyle: 'dashed',
            };
        default:
            return {
                backgroundColor: 'rgba(237, 108, 2, 0.16)',
                borderColor: '#ed6c02',
                color: '#9a4d00',
                left: '14%',
                right: '14%',
                zIndex: 1,
                borderStyle: 'solid',
            };
    }
}

function getIsoWeekLabel(isoDate: string | null | undefined): string | null {
    if (!isoDate) {
        return null;
    }
    const date = new Date(`${isoDate}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    const day = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - day);
    const year = date.getUTCFullYear();
    const yearStart = new Date(Date.UTC(year, 0, 1));
    const week = Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    return `${year}-W${String(week).padStart(2, '0')}`;
}

function getWorkflowWeekKey(workflow: HourReportingWorkflow & Record<string, any>): string | null {
    const workflowContext = (workflow?.workflowContext || {}) as Record<string, any>;
    const declarationStart = workflowContext?.declarationPreview?.period?.start || null;
    const rawDraftDate = Array.isArray(workflowContext?.rawDraftFields?.lines) ? workflowContext.rawDraftFields.lines[0]?.date : null;
    const extractedDate = Array.isArray(workflow?.extractedFields?.draft_lines) ? workflow.extractedFields.draft_lines[0]?.date : null;
    const fallbackDate = workflow?.workDate || workflow?.createdAt?.slice(0, 10) || null;
    return getIsoWeekLabel(declarationStart || rawDraftDate || extractedDate || fallbackDate);
}

function getWorkerDisplayName(workflow: HourReportingWorkflow & Record<string, any>): string {
    const worker = workflow?.worker || null;
    const fullName = [worker?.firstName, worker?.middleName, worker?.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();
    return fullName || String(workflow?.sourcePhoneNumber || 'Unknown worker');
}

function getWorkflowStatusInfo(workflow: HourReportingWorkflow & Record<string, any>): {
    key: string;
    label: string;
    color: 'default' | 'success' | 'warning' | 'error' | 'info';
} {
    const operatorStatus = workflow.operatorStatus;
    const workflowState = String(workflow?.workflowState || '').toLowerCase();
    const status = String(workflow?.status || '').toLowerCase();
    const hasAttention = Boolean(
        operatorStatus?.draftValidationSummary
        || workflow?.lastError
        || (workflow?.observability?.confirmRejects || 0) > 0
        || (workflow?.observability?.validationRejects || 0) > 0,
    );

    if (operatorStatus?.aiPaused || operatorStatus?.adminIntervention) {
        return { key: 'manual', label: 'Manual review', color: 'warning' };
    }
    if (hasAttention) {
        return { key: 'attention', label: 'Needs attention', color: 'error' };
    }
    if (workflow?.submittedAt || status === 'submitted' || status === 'saved' || operatorStatus?.userConfirmed) {
        return { key: 'submitted', label: 'Submitted', color: 'success' };
    }
    if (workflowState === 'awaiting_confirmation') {
        return { key: 'awaiting_confirmation', label: 'Awaiting confirmation', color: 'info' };
    }
    if (workflowState === 'extracting') {
        return { key: 'extracting', label: 'Extracting', color: 'warning' };
    }
    if (workflowState === 'awaiting_hoursheet') {
        return { key: 'awaiting_hoursheet', label: 'Waiting for document', color: 'default' };
    }
    if (workflowState === 'selecting_placement') {
        return { key: 'selecting_placement', label: 'Choose placement', color: 'default' };
    }

    return {
        key: workflowState || status || 'unknown',
        label: workflow?.workflowState || workflow?.status || 'Unknown',
        color: 'default',
    };
}

function getAttachmentPreviewKind(attachment: WorkflowAttachment | null | undefined): 'pdf' | 'image' | null {
    const mimeType = String(attachment?.mimeType || '').toLowerCase();
    if (mimeType === 'application/pdf') {
        return 'pdf';
    }
    if (mimeType.startsWith('image/')) {
        return 'image';
    }
    return null;
}

function getConversationEntry(event: Record<string, any>): ConversationEntry | null {
    const payload = (event?.payload || event?.eventPayload || null) as Record<string, any> | null;
    const eventType = String(event?.eventType || '');
    const message = String(event?.message || '').trim();

    if (eventType === 'hoursheet_received') {
        return {
            id: String(event?.id || `${eventType}-${event?.createdAt || Math.random()}`),
            sender: 'worker',
            title: 'Worker uploaded a document',
            body: payload?.caption || message || null,
            createdAt: event?.createdAt || null,
            attachment: payload,
        };
    }

    if (eventType === 'hours_text_received') {
        return {
            id: String(event?.id || `${eventType}-${event?.createdAt || Math.random()}`),
            sender: 'worker',
            title: 'Worker sent hours as text',
            body: payload?.textPreview || message || null,
            createdAt: event?.createdAt || null,
        };
    }

    if (eventType === 'worker_message_while_paused') {
        return {
            id: String(event?.id || `${eventType}-${event?.createdAt || Math.random()}`),
            sender: 'worker',
            title: 'Worker replied during manual intervention',
            body: payload?.body || message || (payload?.hasFile ? 'Worker sent a file.' : null),
            createdAt: event?.createdAt || null,
        };
    }

    if (eventType === 'admin_message_sent') {
        return {
            id: String(event?.id || `${eventType}-${event?.createdAt || Math.random()}`),
            sender: 'admin',
            title: 'Manual admin reply',
            body: message || null,
            createdAt: event?.createdAt || null,
        };
    }

    if (eventType === 'extraction_completed') {
        const lineCount = Array.isArray(payload?.parsedFields?.lines) ? payload.parsedFields.lines.length : 0;
        const comment = payload?.parsedFields?.header_comment || payload?.parsedFields?.project_notes || null;
        return {
            id: String(event?.id || `${eventType}-${event?.createdAt || Math.random()}`),
            sender: 'assistant',
            title: `Extraction completed${lineCount > 0 ? ` with ${lineCount} worked day${lineCount === 1 ? '' : 's'}` : ''}`,
            body: comment ? `Detected note: ${comment}` : message || null,
            createdAt: event?.createdAt || null,
        };
    }

    if (eventType === 'extraction_failed' || eventType === 'extraction_error') {
        return {
            id: String(event?.id || `${eventType}-${event?.createdAt || Math.random()}`),
            sender: 'assistant',
            title: 'Extraction needs correction',
            body: message || payload?.error || payload?.validationFeedback || null,
            createdAt: event?.createdAt || null,
        };
    }

    if (eventType === 'workflow_created') {
        return {
            id: String(event?.id || `${eventType}-${event?.createdAt || Math.random()}`),
            sender: 'system',
            title: 'Workflow started',
            body: message || null,
            createdAt: event?.createdAt || null,
        };
    }

    if (eventType === 'placement_selected') {
        return {
            id: String(event?.id || `${eventType}-${event?.createdAt || Math.random()}`),
            sender: 'worker',
            title: 'Placement selected',
            body: message || null,
            createdAt: event?.createdAt || null,
        };
    }

    if (eventType === 'workflow_submitted') {
        return {
            id: String(event?.id || `${eventType}-${event?.createdAt || Math.random()}`),
            sender: 'worker',
            title: 'Worker confirmed the draft',
            body: message || null,
            createdAt: event?.createdAt || null,
        };
    }

    return null;
}

function extractDraftCommandLines(command: Record<string, any> | null | undefined): DraftCommandLine[] {
    const draftLines = Array.isArray(command?.draft_lines) ? command.draft_lines : [];
    const commandLines = Array.isArray(command?.lines) ? command.lines : [];
    const sourceLines = draftLines.length ? draftLines : commandLines;

    return sourceLines.map((draftLine: any, index: number) => {
        const matchedCommandLine = commandLines.find((line: any) => line?.date === draftLine?.date) || commandLines[index] || null;
        return {
            date: String(draftLine?.date || matchedCommandLine?.date || ''),
            startTime: draftLine?.start_time || null,
            endTime: draftLine?.end_time || null,
            breakMinutes: draftLine?.break_minutes ?? null,
            workedMinutes: matchedCommandLine?.minutes ?? null,
            remarks: draftLine?.remarks || null,
            looncomponentId: matchedCommandLine?.looncomponentId ?? null,
        };
    }).filter((line) => Boolean(line.date));
}

function JsonAccordion({ title, subtitle, value, defaultExpanded = false }: JsonAccordionProps) {
    return (
        <Accordion defaultExpanded={defaultExpanded} disableGutters elevation={0} sx={{ border: '1px solid #e5d9c0', borderRadius: '16px !important', backgroundColor: '#fffdf8' }}>
            <AccordionSummary expandIcon={<ExpandMoreOutlinedIcon />}>
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
                    {subtitle && (
                        <Typography variant="body2" color="text.secondary">
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
                <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#faf7f0' }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                        {JSON.stringify(value ?? {}, null, 2)}
                    </pre>
                </Paper>
            </AccordionDetails>
        </Accordion>
    );
}

function DetailRow({ label, value }: DetailRowProps) {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 1, py: 0.75 }}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>{value}</Typography>
        </Box>
    );
}

const HourReportingDashboardPage: React.FC = () => {
    const [workflows, setWorkflows] = useState<HourReportingWorkflow[]>([]);
    const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);
    const [detail, setDetail] = useState<HourReportingWorkflowDetail | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDetailLoading, setIsDetailLoading] = useState(false);
    const [draftMessage, setDraftMessage] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState<string | null>(null);
    const [attachmentPreviewError, setAttachmentPreviewError] = useState<string | null>(null);
    const [isAttachmentPreviewLoading, setIsAttachmentPreviewLoading] = useState(false);
    const [isAttachmentDownloading, setIsAttachmentDownloading] = useState(false);
    const [technicalMode, setTechnicalMode] = useState<boolean>(() => {
        if (typeof window === 'undefined') {
            return false;
        }
        return window.localStorage.getItem(TECHNICAL_MODE_STORAGE_KEY) === 'true';
    });
    const [weekFilter, setWeekFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [linkedFilter, setLinkedFilter] = useState('all');

    const availableWeekOptions = useMemo(() => {
        const seen = new Set<string>();
        return workflows
            .map((workflow) => getWorkflowWeekKey(workflow as HourReportingWorkflow & Record<string, any>))
            .filter((value): value is string => Boolean(value))
            .filter((value) => {
                if (seen.has(value)) {
                    return false;
                }
                seen.add(value);
                return true;
            })
            .sort()
            .reverse();
    }, [workflows]);
    const availableStatusOptions = useMemo(() => {
        const seen = new Set<string>();
        return workflows
            .map((workflow) => getWorkflowStatusInfo(workflow as HourReportingWorkflow & Record<string, any>))
            .filter((item) => {
                if (seen.has(item.key)) {
                    return false;
                }
                seen.add(item.key);
                return true;
            });
    }, [workflows]);
    const filteredWorkflows = useMemo(() => workflows.filter((workflow) => {
        const statusInfo = getWorkflowStatusInfo(workflow as HourReportingWorkflow & Record<string, any>);
        const weekKey = getWorkflowWeekKey(workflow as HourReportingWorkflow & Record<string, any>);
        const isLinked = Boolean(workflow?.operatorStatus?.easyflexLinkPresent);

        if (weekFilter !== 'all' && weekKey !== weekFilter) {
            return false;
        }
        if (statusFilter !== 'all' && statusInfo.key !== statusFilter) {
            return false;
        }
        if (linkedFilter === 'linked' && !isLinked) {
            return false;
        }
        if (linkedFilter === 'unlinked' && isLinked) {
            return false;
        }

        return true;
    }), [workflows, weekFilter, statusFilter, linkedFilter]);

    const selectedWorkflow = useMemo(
        () => filteredWorkflows.find((workflow) => workflow.id === selectedWorkflowId) || null,
        [filteredWorkflows, selectedWorkflowId],
    );
    const workflow = detail?.workflow || null;
    const workflowContext = workflow?.workflowContext || {};
    const observability = workflow?.observability || null;
    const extractionDebug = workflowContext?.lastExtractionDebug || null;
    const declarationPreview = workflowContext?.declarationPreview || null;
    const rawDraftFields = workflowContext?.rawDraftFields || null;
    const extractedCommand = (workflow?.extractedFields || null) as Record<string, any> | null;
    const confirmedCommand = (workflow?.confirmedFields || null) as Record<string, any> | null;
    const currentCommand = (workflow?.confirmedFields || workflow?.extractedFields || null) as Record<string, any> | null;
    const extractedCommandLines = useMemo(() => extractDraftCommandLines(extractedCommand), [extractedCommand]);
    const confirmedCommandLines = useMemo(() => extractDraftCommandLines(confirmedCommand), [confirmedCommand]);
    const commandLines = useMemo(() => extractDraftCommandLines(currentCommand), [currentCommand]);
    const totalWorkedMinutes = useMemo(
        () => commandLines.reduce((sum, line) => sum + Math.max(0, Number(line.workedMinutes || 0)), 0),
        [commandLines],
    );
    const attachment = (workflow?.attachment || null) as WorkflowAttachment | null;
    const attachmentPreviewKind = useMemo(() => getAttachmentPreviewKind(attachment), [attachment]);
    const conversationEntries = useMemo(
        () => (detail?.events || []).map((event) => getConversationEntry(event)).filter((entry): entry is ConversationEntry => Boolean(entry)),
        [detail?.events],
    );
    const isApproved = useMemo(() => {
        const normalizedStatus = String(workflow?.status || workflow?.workflowState || '').toLowerCase();
        return Boolean(workflow?.submittedAt) || normalizedStatus === 'submitted' || normalizedStatus === 'saved';
    }, [workflow?.status, workflow?.workflowState, workflow?.submittedAt]);
    const hoursDiagram = useMemo(() => buildHoursDiagramData({
        declarationPeriodStart: declarationPreview?.period?.start || commandLines[0]?.date || null,
        extractedLines: extractedCommandLines,
        confirmedLines: confirmedCommandLines,
        isApproved,
        hasAttentionState: Boolean(
            workflow?.operatorStatus?.draftValidationSummary
            || (observability?.confirmRejects || 0) > 0
            || (observability?.validationRejects || 0) > 0
            || (Array.isArray(observability?.lastValidationIssues) && observability.lastValidationIssues.length > 0),
        ),
    }), [
        declarationPreview?.period?.start,
        commandLines,
        extractedCommandLines,
        confirmedCommandLines,
        isApproved,
        workflow?.operatorStatus?.draftValidationSummary,
        observability?.confirmRejects,
        observability?.validationRejects,
        observability?.lastValidationIssues,
    ]);
    const selectedWorkerLabel = useMemo(
        () => getWorkerDisplayName((workflow || selectedWorkflow || {}) as HourReportingWorkflow & Record<string, any>),
        [workflow, selectedWorkflow],
    );

    const refreshList = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await listHourReportingWorkflows(100);
            setWorkflows(data);
            if (!selectedWorkflowId && data.length) {
                setSelectedWorkflowId(data[0].id);
            }
        } catch (err: any) {
            setError(err?.message || 'Failed to load hour-reporting workflows');
        } finally {
            setIsLoading(false);
        }
    };

    const refreshDetail = async (workflowId: string) => {
        setIsDetailLoading(true);
        setError(null);
        try {
            const data = await getHourReportingWorkflow(workflowId);
            setDetail(data);
        } catch (err: any) {
            setError(err?.message || 'Failed to load workflow detail');
        } finally {
            setIsDetailLoading(false);
        }
    };

    useEffect(() => {
        refreshList();
    }, []);

    useEffect(() => {
        if (selectedWorkflowId) {
            refreshDetail(selectedWorkflowId);
        } else {
            setDetail(null);
        }
    }, [selectedWorkflowId]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        window.localStorage.setItem(TECHNICAL_MODE_STORAGE_KEY, technicalMode ? 'true' : 'false');
    }, [technicalMode]);

    useEffect(() => {
        if (filteredWorkflows.length === 0) {
            if (selectedWorkflowId !== null) {
                setSelectedWorkflowId(null);
            }
            return;
        }
        if (!filteredWorkflows.some((workflow) => workflow.id === selectedWorkflowId)) {
            setSelectedWorkflowId(filteredWorkflows[0].id);
        }
    }, [filteredWorkflows, selectedWorkflowId]);

    useEffect(() => {
        let isCancelled = false;
        let nextObjectUrl: string | null = null;

        setAttachmentPreviewUrl((current) => {
            if (current) {
                URL.revokeObjectURL(current);
            }
            return null;
        });
        setAttachmentPreviewError(null);

        if (!attachment?.savedFileId || !attachmentPreviewKind) {
            setIsAttachmentPreviewLoading(false);
            return () => undefined;
        }

        setIsAttachmentPreviewLoading(true);

        axiosInstance.get(`/worker-files/${attachment.savedFileId}/preview`, { responseType: 'blob' })
            .then((response) => {
                if (isCancelled) {
                    return;
                }
                nextObjectUrl = URL.createObjectURL(response.data);
                setAttachmentPreviewUrl(nextObjectUrl);
            })
            .catch((err: any) => {
                if (isCancelled) {
                    return;
                }
                setAttachmentPreviewError(err?.message || 'Failed to load attachment preview');
            })
            .finally(() => {
                if (!isCancelled) {
                    setIsAttachmentPreviewLoading(false);
                }
            });

        return () => {
            isCancelled = true;
            if (nextObjectUrl) {
                URL.revokeObjectURL(nextObjectUrl);
            }
        };
    }, [attachment?.savedFileId, attachmentPreviewKind]);

    const runAction = async (fn: () => Promise<void>) => {
        if (!selectedWorkflowId) return;
        setError(null);
        try {
            await fn();
            await Promise.all([refreshList(), refreshDetail(selectedWorkflowId)]);
        } catch (err: any) {
            setError(err?.message || 'Action failed');
        }
    };

    const handleAttachmentDownload = async () => {
        if (!attachment?.savedFileId) return;

        setIsAttachmentDownloading(true);
        try {
            const response = await axiosInstance.get(`/worker-files/${attachment.savedFileId}/download`, {
                responseType: 'blob',
            });
            const blobUrl = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = attachment.fileName || 'timesheet-attachment';
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(blobUrl);
        } catch (err: any) {
            setAttachmentPreviewError(err?.message || 'Failed to download attachment');
        } finally {
            setIsAttachmentDownloading(false);
        }
    };

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: '380px 1fr' },
                gap: 2,
                minHeight: '100%',
                p: 2,
                background: 'linear-gradient(180deg, #f5efe2 0%, #f7f6f2 100%)',
            }}
        >
            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #d7c9ad', overflow: 'hidden' }}>
                <Box sx={{ p: 2.5, borderBottom: '1px solid #eadfc8', backgroundColor: '#fbf3e4' }}>
                    <Typography variant="overline" sx={{ letterSpacing: '0.12em', color: '#8c5a1f' }}>
                        Hour Reporting Inbox
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        WhatsApp Hourpapers
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Track linkage, proof upload, text extraction, confirmation, and approval before Easyflex sync.
                    </Typography>
                </Box>

                <Box sx={{ p: 2, borderBottom: '1px solid #eadfc8', backgroundColor: '#fffaf0' }}>
                    <Stack spacing={1.25}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Queue filters
                        </Typography>
                        <TextField
                            select
                            size="small"
                            label="Week"
                            value={weekFilter}
                            onChange={(event) => setWeekFilter(event.target.value)}
                            SelectProps={{ native: true }}
                        >
                            <option value="all">All weeks</option>
                            {availableWeekOptions.map((option) => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </TextField>
                        <TextField
                            select
                            size="small"
                            label="Status"
                            value={statusFilter}
                            onChange={(event) => setStatusFilter(event.target.value)}
                            SelectProps={{ native: true }}
                        >
                            <option value="all">All statuses</option>
                            {availableStatusOptions.map((option) => (
                                <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                        </TextField>
                        <TextField
                            select
                            size="small"
                            label="Link state"
                            value={linkedFilter}
                            onChange={(event) => setLinkedFilter(event.target.value)}
                            SelectProps={{ native: true }}
                        >
                            <option value="all">All workflows</option>
                            <option value="linked">Linked only</option>
                            <option value="unlinked">Unlinked only</option>
                        </TextField>
                        <Typography variant="caption" color="text.secondary">
                            Showing {filteredWorkflows.length} of {workflows.length} workflow(s)
                        </Typography>
                    </Stack>
                </Box>

                {isLoading ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
                        <CircularProgress size={28} />
                    </Stack>
                ) : (
                    <List sx={{ p: 0 }}>
                        {filteredWorkflows.map((item) => {
                            const statusInfo = getWorkflowStatusInfo(item as HourReportingWorkflow & Record<string, any>);
                            const workerName = getWorkerDisplayName(item as HourReportingWorkflow & Record<string, any>);
                            const weekKey = getWorkflowWeekKey(item as HourReportingWorkflow & Record<string, any>);
                            return (
                                <ListItemButton
                                    key={item.id}
                                    selected={item.id === selectedWorkflowId}
                                    onClick={() => setSelectedWorkflowId(item.id)}
                                    sx={{ alignItems: 'flex-start', py: 2, px: 2.5, borderBottom: '1px solid #f0e7d5' }}
                                >
                                    <ListItemText
                                        primary={(
                                            <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography fontWeight={700} noWrap>{workerName}</Typography>
                                                    {workerName !== item.sourcePhoneNumber && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {item.sourcePhoneNumber}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Chip size="small" color={statusInfo.color} label={statusInfo.label} />
                                            </Stack>
                                        )}
                                        secondaryTypographyProps={{ component: 'div' }}
                                        secondary={(
                                            <Stack spacing={1} sx={{ mt: 1 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {item.placementLabel || item.workflowContext?.declarationPreview?.placementLabel || 'No declaration linked yet'}
                                                </Typography>
                                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                    {weekKey && <Chip size="small" label={weekKey} />}
                                                    <Chip size="small" color={item.operatorStatus.easyflexLinkPresent ? 'success' : 'default'} label={item.operatorStatus.easyflexLinkPresent ? 'Linked' : 'Unlinked'} />
                                                    <Chip size="small" color={item.operatorStatus.imageSubmitted ? 'success' : 'default'} label={item.operatorStatus.imageSubmitted ? 'File in' : 'No file'} />
                                                    <Chip size="small" color={item.operatorStatus.textSubmitted ? 'success' : 'default'} label={item.operatorStatus.textSubmitted ? 'Text in' : 'No text'} />
                                                    <Chip size="small" color={item.operatorStatus.userConfirmed ? 'success' : 'default'} label={item.operatorStatus.userConfirmed ? 'Confirmed' : 'Unconfirmed'} />
                                                </Stack>
                                            </Stack>
                                        )}
                                    />
                                </ListItemButton>
                            );
                        })}
                        {filteredWorkflows.length === 0 && (
                            <Box sx={{ p: 2.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                    No workflows match the current filters.
                                </Typography>
                            </Box>
                        )}
                    </List>
                )}
            </Paper>

            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #d7c9ad', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2.5, borderBottom: '1px solid #eadfc8', backgroundColor: '#fffaf0' }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                        <Box>
                            <Typography variant="overline" sx={{ letterSpacing: '0.12em', color: '#8c5a1f' }}>
                                Intervention
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                {selectedWorkflowId ? selectedWorkerLabel : 'Select a workflow'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedWorkflow?.placementLabel || 'Inspect extraction source, draft state, declaration preview, and sync controls for the selected thread.'}
                            </Typography>
                        </Box>
                        <Stack spacing={1} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
                            <FormControlLabel
                                control={(
                                    <Switch
                                        checked={technicalMode}
                                        onChange={(event) => setTechnicalMode(event.target.checked)}
                                        color="warning"
                                    />
                                )}
                                label="Technical mode"
                                sx={{
                                    m: 0,
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: 14,
                                        fontWeight: 700,
                                    },
                                }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 280 }}>
                                {technicalMode
                                    ? 'Raw logs, event payloads, and workflow internals are visible.'
                                    : 'Showing the operator-friendly view. Turn this on for raw logs and debugging payloads.'}
                            </Typography>
                            {selectedWorkflow && (
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ md: 'flex-end' }}>
                                    <Button variant="outlined" startIcon={<PauseCircleOutlineOutlinedIcon />} onClick={() => runAction(() => pauseHourReportingWorkflow(selectedWorkflow.id))}>
                                        Pause
                                    </Button>
                                    <Button variant="outlined" onClick={() => runAction(() => resumeHourReportingWorkflow(selectedWorkflow.id))}>
                                        Resume
                                    </Button>
                                    <Button variant="contained" color="success" startIcon={<CheckCircleOutlineOutlinedIcon />} onClick={() => runAction(() => approveHourReportingWorkflow(selectedWorkflow.id))}>
                                        Approve Sync
                                    </Button>
                                </Stack>
                            )}
                        </Stack>
                    </Stack>
                </Box>

                {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

                {!selectedWorkflowId ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ p: 6 }}>
                        <Typography color="text.secondary">Select a workflow to inspect the hour-reporting thread.</Typography>
                    </Stack>
                ) : isDetailLoading ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ p: 6 }}>
                        <CircularProgress size={28} />
                    </Stack>
                ) : (
                    <>
                        <Box sx={{ p: 2.5 }}>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip icon={<LinkOutlinedIcon />} label={workflowContext?.approvalStatus || 'no approval state'} />
                                <Chip icon={<AccessTimeOutlinedIcon />} label={workflowContext?.easyflexSyncStatus || 'no sync state'} />
                                <Chip label={workflow?.workflowState || 'unknown workflow'} />
                                <Chip label={`mode ${observability?.lastExtractionKind || 'n/a'}`} />
                                <Chip label={`extract ok ${observability?.extractionSuccesses || 0}/${observability?.extractionAttempts || 0}`} />
                                <Chip label={`corrections ${observability?.correctionCount || 0}`} />
                                <Chip label={`confirm ok ${observability?.confirmSuccesses || 0}/${observability?.confirmAttempts || 0}`} />
                            </Stack>

                            {workflow?.operatorStatus?.draftValidationSummary && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    {workflow.operatorStatus.draftValidationSummary}
                                </Alert>
                            )}

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.2fr) minmax(340px, 0.8fr)' }, gap: 2, mt: 2 }}>
                                <Stack spacing={2}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, backgroundColor: '#fffdf8' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                                            Overview
                                        </Typography>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
                                            <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#faf7f0' }}>
                                                <Typography variant="caption" color="text.secondary">Worker</Typography>
                                                <Typography sx={{ fontWeight: 700 }}>{selectedWorkerLabel}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {workflow?.sourcePhoneNumber || formatPrimitive(workflow?.workerId)}
                                                </Typography>
                                            </Paper>
                                            <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#faf7f0' }}>
                                                <Typography variant="caption" color="text.secondary">Workflow</Typography>
                                                <Typography sx={{ fontWeight: 700 }}>{formatPrimitive(workflow?.workflowState)}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Approval {formatPrimitive(workflowContext?.approvalStatus || 'pending')}
                                                </Typography>
                                            </Paper>
                                            <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#faf7f0' }}>
                                                <Typography variant="caption" color="text.secondary">Reported hours</Typography>
                                                <Typography sx={{ fontWeight: 700 }}>{formatWorkedMinutes(totalWorkedMinutes)}</Typography>
                                                <Typography variant="body2" color="text.secondary">{commandLines.length} line(s) in current draft</Typography>
                                            </Paper>
                                            <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#faf7f0' }}>
                                                <Typography variant="caption" color="text.secondary">Document</Typography>
                                                <Typography sx={{ fontWeight: 700 }}>{attachment?.fileName ? 'Available' : 'Missing'}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {attachment?.fileName ? `${attachment.mimeType || 'unknown'} · ${formatFileSize(attachment.sizeBytes)}` : 'No uploaded hourpaper yet'}
                                                </Typography>
                                            </Paper>
                                        </Box>
                                    </Paper>

                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, backgroundColor: '#fffdf8' }}>
                                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 1.5 }}>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                    Weekly hours map
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Monday-to-Sunday time grid for extracted, corrected, and submitted hours.
                                                </Typography>
                                            </Box>
                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                <Chip size="small" label="Reported" sx={{ backgroundColor: 'rgba(237, 108, 2, 0.16)', color: '#9a4d00', border: '1px solid #ed6c02' }} />
                                                <Chip size="small" label="Disputed" sx={{ backgroundColor: 'rgba(211, 47, 47, 0.16)', color: '#8e1c1c', border: '1px dashed #d32f2f' }} />
                                                <Chip size="small" label="Corrected" sx={{ backgroundColor: 'rgba(2, 136, 209, 0.18)', color: '#01579b', border: '1px solid #0288d1' }} />
                                                <Chip size="small" label="Approved" sx={{ backgroundColor: 'rgba(46, 125, 50, 0.24)', color: '#1b5e20', border: '1px solid #2e7d32' }} />
                                            </Stack>
                                        </Stack>

                                        <Typography variant="body2" color="text.secondary">
                                            {commandLines.length} plotted line(s) · {formatWorkedMinutes(totalWorkedMinutes)} total
                                        </Typography>
                                        {hoursDiagram.missingLineCount > 0 && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                {hoursDiagram.missingLineCount} line(s) were not plotted because start or end time is missing.
                                            </Typography>
                                        )}
                                        {hoursDiagram.hasAttentionState && (
                                            <Alert severity="warning" sx={{ mt: 1.5 }}>
                                                This workflow still has validation or confirmation attention points. Red blocks show extracted lines that were changed or rejected.
                                            </Alert>
                                        )}

                                        <Box sx={{ mt: 1.75, overflowX: 'auto', pb: 1 }}>
                                            <Box sx={{ minWidth: 940 }}>
                                                <Box
                                                    sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '72px repeat(7, minmax(110px, 1fr))',
                                                        gap: 0,
                                                        mb: 1,
                                                    }}
                                                >
                                                    <Box />
                                                    {hoursDiagram.weekDates.map((isoDate) => (
                                                        <Box key={`header-${isoDate}`} sx={{ px: 1, pb: 1 }}>
                                                            <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: '#8c5a1f' }}>
                                                                {formatWeekdayLabel(isoDate)}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                {formatShortDayLabel(isoDate)}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>

                                                <Box
                                                    sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '72px repeat(7, minmax(110px, 1fr))',
                                                        gap: 0,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            position: 'relative',
                                                            height: `${(hoursDiagram.maxHour - hoursDiagram.minHour) * 44}px`,
                                                            borderRight: '1px solid #eadfc8',
                                                        }}
                                                    >
                                                        {Array.from({ length: hoursDiagram.maxHour - hoursDiagram.minHour + 1 }, (_, offset) => {
                                                            const hour = hoursDiagram.minHour + offset;
                                                            return (
                                                                <Typography
                                                                    key={`time-${hour}`}
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        top: `${Math.max(0, (offset * 44) - 8)}px`,
                                                                        right: 10,
                                                                    }}
                                                                >
                                                                    {`${String(hour).padStart(2, '0')}:00`}
                                                                </Typography>
                                                            );
                                                        })}
                                                    </Box>

                                                    {hoursDiagram.weekDates.map((isoDate, dayIndex) => (
                                                        <Box
                                                            key={`day-${isoDate}`}
                                                            sx={{
                                                                position: 'relative',
                                                                height: `${(hoursDiagram.maxHour - hoursDiagram.minHour) * 44}px`,
                                                                borderLeft: '1px solid #eadfc8',
                                                                borderTop: '1px solid #eadfc8',
                                                                borderBottom: '1px solid #eadfc8',
                                                                backgroundImage: 'repeating-linear-gradient(to bottom, transparent, transparent 43px, rgba(140, 90, 31, 0.08) 43px, rgba(140, 90, 31, 0.08) 44px)',
                                                                backgroundColor: dayIndex % 2 === 0 ? '#fffaf0' : '#fffdf8',
                                                            }}
                                                        >
                                                            {hoursDiagram.blocks
                                                                .filter((block) => block.dayIndex === dayIndex)
                                                                .map((block) => {
                                                                    const top = ((block.startMinutes - (hoursDiagram.minHour * 60)) / 60) * 44;
                                                                    const height = Math.max(26, ((block.endMinutes - block.startMinutes) / 60) * 44);
                                                                    const blockStyle = getHoursBlockStyle(block.status);
                                                                    return (
                                                                        <Box
                                                                            key={block.id}
                                                                            sx={{
                                                                                position: 'absolute',
                                                                                top: `${top}px`,
                                                                                height: `${height}px`,
                                                                                borderRadius: 2,
                                                                                borderWidth: '1px',
                                                                                borderStyle: String(blockStyle.borderStyle),
                                                                                borderColor: String(blockStyle.borderColor),
                                                                                backgroundColor: String(blockStyle.backgroundColor),
                                                                                color: String(blockStyle.color),
                                                                                left: String(blockStyle.left),
                                                                                right: String(blockStyle.right),
                                                                                zIndex: Number(blockStyle.zIndex),
                                                                                px: 0.75,
                                                                                py: 0.5,
                                                                                overflow: 'hidden',
                                                                                boxShadow: '0 6px 12px rgba(15, 23, 42, 0.05)',
                                                                            }}
                                                                        >
                                                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, lineHeight: 1.15 }}>
                                                                                {block.timeLabel}
                                                                            </Typography>
                                                                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.92, lineHeight: 1.15 }}>
                                                                                {block.workedLabel}
                                                                            </Typography>
                                                                            {height >= 48 && block.remarks && (
                                                                                <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.1, mt: 0.25, opacity: 0.85 }}>
                                                                                    {block.remarks}
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    );
                                                                })}
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Paper>

                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, backgroundColor: '#fffdf8' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Conversation</Typography>
                                        <Stack spacing={1.25}>
                                            {conversationEntries.length === 0 ? (
                                                <Typography variant="body2" color="text.secondary">
                                                    No recorded conversation events yet. Use the sandbox page for a full worker-style chat simulation.
                                                </Typography>
                                            ) : conversationEntries.map((entry) => {
                                                const isWorker = entry.sender === 'worker';
                                                const isAssistant = entry.sender === 'assistant';
                                                const isAdmin = entry.sender === 'admin';
                                                return (
                                                    <Paper
                                                        key={entry.id}
                                                        variant="outlined"
                                                        sx={{
                                                            p: 1.5,
                                                            backgroundColor: isWorker
                                                                ? '#f6efe3'
                                                                : isAssistant
                                                                    ? '#eef6ef'
                                                                    : isAdmin
                                                                        ? '#eef3fb'
                                                                        : '#faf7f0',
                                                        }}
                                                    >
                                                        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                {isWorker ? (
                                                                    <PersonOutlineOutlinedIcon fontSize="small" />
                                                                ) : isAssistant ? (
                                                                    <SmartToyOutlinedIcon fontSize="small" />
                                                                ) : (
                                                                    <AccessTimeOutlinedIcon fontSize="small" />
                                                                )}
                                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                    {entry.title}
                                                                </Typography>
                                                            </Stack>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {formatDateTime(entry.createdAt)}
                                                            </Typography>
                                                        </Stack>
                                                        {entry.body && (
                                                            <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
                                                                {entry.body}
                                                            </Typography>
                                                        )}
                                                        {entry.attachment?.fileName && (
                                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                                                                <DescriptionOutlinedIcon fontSize="small" />
                                                                <Typography variant="body2">
                                                                    {entry.attachment.fileName} ({formatFileSize(entry.attachment.sizeBytes)})
                                                                </Typography>
                                                            </Stack>
                                                        )}
                                                    </Paper>
                                                );
                                            })}
                                        </Stack>
                                    </Paper>

                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, backgroundColor: '#fffdf8' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Reported days</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {commandLines.length} line(s) · {formatWorkedMinutes(totalWorkedMinutes)} total
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            Comment: {currentCommand?.header?.comment || currentCommand?.header_comment || currentCommand?.project_notes || 'empty'}
                                        </Typography>

                                        <Stack spacing={1} sx={{ mt: 1.5 }}>
                                            {commandLines.length === 0 ? (
                                                <Typography variant="body2" color="text.secondary">No extracted or confirmed command yet.</Typography>
                                            ) : commandLines.map((line, index) => (
                                                <Paper key={`${line.date}-${line.startTime || 'na'}-${line.endTime || 'na'}-${index}`} variant="outlined" sx={{ p: 1.5, backgroundColor: '#faf7f0' }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{line.date}</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {line.startTime && line.endTime ? `${line.startTime}-${line.endTime}` : 'time missing'} · break {line.breakMinutes ?? 0}m · worked {formatWorkedMinutes(line.workedMinutes)}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Looncomponent: {line.looncomponentId || 'missing'}
                                                    </Typography>
                                                    {line.remarks && (
                                                        <Typography variant="body2" sx={{ mt: 0.5 }}>{line.remarks}</Typography>
                                                    )}
                                                </Paper>
                                            ))}
                                        </Stack>
                                    </Paper>

                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, backgroundColor: '#fffdf8' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Observability</Typography>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
                                            <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#faf7f0' }}>
                                                <Typography variant="caption" color="text.secondary">Extraction</Typography>
                                                <Typography sx={{ fontWeight: 700 }}>{observability?.lastExtractionKind || 'n/a'}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {formatPrimitive(observability?.lastExtractionProcessedBy)} · {formatPrimitive(observability?.lastExtractionModel)}
                                                </Typography>
                                            </Paper>
                                            <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#faf7f0' }}>
                                                <Typography variant="caption" color="text.secondary">Confidence</Typography>
                                                <Typography sx={{ fontWeight: 700 }}>
                                                    {observability?.lastExtractionConfidence ?? 'n/a'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Requested {formatDateTime(observability?.lastExtractionRequestedAt)}
                                                </Typography>
                                            </Paper>
                                            <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#faf7f0' }}>
                                                <Typography variant="caption" color="text.secondary">Corrections / Confirm</Typography>
                                                <Typography sx={{ fontWeight: 700 }}>
                                                    {observability?.correctionCount || 0} / {observability?.confirmSuccesses || 0}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Confirm rejects {observability?.confirmRejects || 0}
                                                </Typography>
                                            </Paper>
                                            <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#faf7f0' }}>
                                                <Typography variant="caption" color="text.secondary">Validation</Typography>
                                                <Typography sx={{ fontWeight: 700 }}>
                                                    Rejects {observability?.validationRejects || 0}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Duplicate date rejects {observability?.duplicateDateRejects || 0}
                                                </Typography>
                                            </Paper>
                                        </Box>
                                        {Array.isArray(observability?.lastValidationIssues) && observability.lastValidationIssues.length > 0 && (
                                            <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                                                {observability.lastValidationIssues.map((issue: any, index: number) => (
                                                    <Paper key={`${issue?.code || 'issue'}-${index}`} variant="outlined" sx={{ p: 1.25, backgroundColor: '#fff7ef' }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{issue?.code || 'validation_issue'}</Typography>
                                                        <Typography variant="body2" color="text.secondary">{issue?.message || 'No message'}</Typography>
                                                    </Paper>
                                                ))}
                                            </Stack>
                                        )}
                                    </Paper>

                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, backgroundColor: '#fffdf8' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Workflow metadata</Typography>
                                        <DetailRow label="Workflow" value={formatPrimitive(workflow?.id)} />
                                        <DetailRow label="Worker" value={formatPrimitive(workflow?.workerId)} />
                                        <DetailRow label="Placement" value={formatPrimitive(workflow?.placementLabel)} />
                                        <DetailRow label="File Submitted" value={workflow?.operatorStatus?.imageSubmitted ? 'yes' : 'no'} />
                                        <DetailRow label="Text Submitted" value={workflow?.operatorStatus?.textSubmitted ? 'yes' : 'no'} />
                                        <DetailRow label="Workflow Language" value={workflow?.workflowLanguage || 'unknown'} />
                                        <DetailRow label="Provider / Model" value={`${formatPrimitive(workflow?.aiProvider)} / ${formatPrimitive(workflow?.aiModel)}`} />
                                        <DetailRow label="Approval" value={formatPrimitive(workflowContext?.approvalStatus)} />
                                        <DetailRow label="Sync" value={formatPrimitive(workflowContext?.easyflexSyncStatus)} />
                                        <DetailRow label="Created" value={formatDateTime(workflow?.createdAt)} />
                                        <DetailRow label="Updated" value={formatDateTime(workflow?.updatedAt)} />
                                        <DetailRow label="Attachment" value={attachment?.fileName ? `${attachment.fileName} (${attachment.mimeType || 'unknown'})` : 'missing'} />
                                    </Paper>
                                </Stack>

                                <Stack spacing={2}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, backgroundColor: '#fffdf8' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Source document</Typography>
                                        {!attachment?.fileName ? (
                                            <Typography variant="body2" color="text.secondary">
                                                No attachment is linked to this workflow yet.
                                            </Typography>
                                        ) : (
                                            <Stack spacing={1.5}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    {attachmentPreviewKind === 'image' ? (
                                                        <ImageOutlinedIcon fontSize="small" />
                                                    ) : (
                                                        <DescriptionOutlinedIcon fontSize="small" />
                                                    )}
                                                    <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                                                        {attachment.fileName}
                                                    </Typography>
                                                </Stack>
                                                <Typography variant="body2" color="text.secondary">
                                                    {attachment.mimeType || 'unknown type'} · {formatFileSize(attachment.sizeBytes)}
                                                </Typography>
                                                {attachment.caption && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Caption: {attachment.caption}
                                                    </Typography>
                                                )}
                                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                    {attachmentPreviewUrl && (
                                                        <Button
                                                            component="a"
                                                            href={attachmentPreviewUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            variant="outlined"
                                                        >
                                                            Open document
                                                        </Button>
                                                    )}
                                                    {attachment?.savedFileId && (
                                                        <Button
                                                            variant="contained"
                                                            startIcon={<DownloadOutlinedIcon />}
                                                            onClick={handleAttachmentDownload}
                                                            disabled={isAttachmentDownloading}
                                                        >
                                                            {isAttachmentDownloading ? 'Downloading...' : 'Download document'}
                                                        </Button>
                                                    )}
                                                    {!attachment?.savedFileId && attachment?.fileName && (
                                                        <Button variant="contained" disabled>
                                                            Download unavailable
                                                        </Button>
                                                    )}
                                                </Stack>
                                                {isAttachmentPreviewLoading && (
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <CircularProgress size={18} />
                                                        <Typography variant="body2" color="text.secondary">
                                                            Loading attachment preview...
                                                        </Typography>
                                                    </Stack>
                                                )}
                                                {attachmentPreviewError && (
                                                    <Alert severity="warning">{attachmentPreviewError}</Alert>
                                                )}
                                                {!attachment?.savedFileId && (
                                                    <Alert severity="info">
                                                        This workflow only has attachment metadata. Download and inline preview become available once the upload is stored as a worker file.
                                                    </Alert>
                                                )}
                                                {attachmentPreviewKind === 'pdf' && attachmentPreviewUrl && (
                                                    <Box
                                                        component="iframe"
                                                        src={attachmentPreviewUrl}
                                                        title={attachment.fileName || 'Attachment preview'}
                                                        sx={{
                                                            width: '100%',
                                                            minHeight: 520,
                                                            border: '1px solid #e5d9c0',
                                                            borderRadius: 2,
                                                            backgroundColor: '#fff',
                                                        }}
                                                    />
                                                )}
                                                {attachmentPreviewKind === 'image' && attachmentPreviewUrl && (
                                                    <Box
                                                        component="img"
                                                        src={attachmentPreviewUrl}
                                                        alt={attachment.fileName || 'Attachment preview'}
                                                        sx={{
                                                            width: '100%',
                                                            maxHeight: 520,
                                                            objectFit: 'contain',
                                                            border: '1px solid #e5d9c0',
                                                            borderRadius: 2,
                                                            backgroundColor: '#fff',
                                                        }}
                                                    />
                                                )}
                                            </Stack>
                                        )}
                                    </Paper>

                                    {technicalMode ? (
                                        <>
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, backgroundColor: '#fffdf8' }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Technical details</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Raw workflow diagnostics, extraction payloads, and event data are enabled for debugging.
                                                </Typography>
                                            </Paper>
                                            <JsonAccordion
                                                title="Raw Draft Fields"
                                                subtitle="Normalized draft the workflow is currently using for validation and corrections."
                                                value={rawDraftFields}
                                                defaultExpanded
                                            />
                                            <JsonAccordion
                                                title="Extraction Debug"
                                                subtitle="Full AI request/response trace for the latest extraction attempt."
                                                value={extractionDebug}
                                            />
                                            <JsonAccordion
                                                title="Declaration Preview"
                                                subtitle="Editable declaration context loaded from CentralDB/Easyflex."
                                                value={declarationPreview}
                                            />
                                            <JsonAccordion
                                                title="Command Payload"
                                                subtitle="Canonical declaration command that would be submitted or has been submitted."
                                                value={currentCommand}
                                            />
                                        </>
                                    ) : (
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, backgroundColor: '#fffdf8' }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>Technical details hidden</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Turn on technical mode to inspect raw draft fields, extraction payloads, declaration previews, and command JSON.
                                            </Typography>
                                        </Paper>
                                    )}
                                </Stack>
                            </Box>
                        </Box>

                        {technicalMode && (
                            <>
                                <Divider />

                                <Box sx={{ p: 2.5 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                                        Timeline
                                    </Typography>
                                    <Stack spacing={1.5}>
                                        {(detail?.events || []).map((event) => {
                                            const eventPayload = event.payload || event.eventPayload || null;
                                            return (
                                                <Paper key={event.id} variant="outlined" sx={{ p: 1.5, backgroundColor: '#fffdf8' }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {new Date(event.createdAt).toLocaleString()}
                                                    </Typography>
                                                    <Typography sx={{ fontWeight: 700 }}>{event.eventType}</Typography>
                                                    {event.message && (
                                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                            {event.message}
                                                        </Typography>
                                                    )}
                                                    {eventPayload && (
                                                        <JsonAccordion
                                                            title="Event Payload"
                                                            subtitle="Recorded payload for this workflow event."
                                                            value={eventPayload}
                                                        />
                                                    )}
                                                </Paper>
                                            );
                                        })}
                                    </Stack>
                                </Box>
                            </>
                        )}

                        <Divider />

                        <Box sx={{ p: 2.5, backgroundColor: '#fffaf0' }}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                                <TextField
                                    fullWidth
                                    label="Manual reply"
                                    value={draftMessage}
                                    onChange={(event) => setDraftMessage(event.target.value)}
                                    placeholder="Ask for a clearer hourpaper, tell the worker to resend text with dates, or confirm the correction they need to make."
                                />
                                <Button
                                    variant="contained"
                                    onClick={() => runAction(async () => {
                                        if (!selectedWorkflowId) return;
                                        await sendHourReportingAdminMessage(selectedWorkflowId, draftMessage);
                                        setDraftMessage('');
                                    })}
                                    disabled={!draftMessage.trim()}
                                >
                                    Send
                                </Button>
                            </Stack>
                        </Box>
                    </>
                )}
            </Paper>
        </Box>
    );
};

export default HourReportingDashboardPage;
